import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "procurex_theme";
const VALID = new Set(["light", "dark"]);

const detectInitial = () => {
    // 1) Stored explicit preference
    try {
        const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
        if (saved && VALID.has(saved)) return saved;
    } catch (err) {
        // Private/incognito modes may throw on access. Fall back to system preference.
        console.debug("theme: localStorage read failed", err?.message || err);
    }
    // 2) System preference (default to LIGHT if unsure — product calls for whitespace-heavy light UI)
    try {
        if (typeof window !== "undefined" && window.matchMedia) {
            if (window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
        }
    } catch (err) {
        console.debug("theme: matchMedia failed", err?.message || err);
    }
    return "light";
};

const applyTheme = (theme) => {
    const root = typeof document !== "undefined" ? document.documentElement : null;
    if (!root) return;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    // Hint to UA for native form controls / scrollbars
    root.style.colorScheme = theme;
};

export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(detectInitial);

    // Apply on mount + any change
    useEffect(() => {
        applyTheme(theme);
    }, [theme]);

    // React to system preference changes only if user has not chosen explicitly.
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = (e) => {
            try {
                const saved = localStorage.getItem(STORAGE_KEY);
                if (saved && VALID.has(saved)) return; // explicit choice wins
            } catch (err) {
                console.debug("theme: storage read failed in mq handler", err?.message || err);
            }
            setThemeState(e.matches ? "dark" : "light");
        };
        try { mq.addEventListener("change", onChange); } catch {
            // Legacy Safari (<14) uses addListener.
            mq.addListener(onChange);
        }
        return () => {
            try { mq.removeEventListener("change", onChange); } catch {
                mq.removeListener(onChange);
            }
        };
    }, []);

    const setTheme = useCallback((next) => {
        if (!VALID.has(next)) return;
        setThemeState(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch (err) {
            console.debug("theme: persist failed", err?.message || err);
        }
    }, []);

    const toggleTheme = useCallback(() => {
        setThemeState((curr) => {
            const next = curr === "dark" ? "light" : "dark";
            try { localStorage.setItem(STORAGE_KEY, next); } catch (err) {
                console.debug("theme: persist failed", err?.message || err);
            }
            return next;
        });
    }, []);

    const value = useMemo(
        () => ({ theme, isDark: theme === "dark", setTheme, toggleTheme }),
        [theme, setTheme, toggleTheme]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
    return ctx;
};
